import React, { useCallback, useEffect, useState } from "react";
import MUICard from "../../UI/Card/MUICard";
import {
  Box,
  Chip,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";

import Editor from "@monaco-editor/react";
import { defaultCodes } from "../../../utils/defaultCodes";
import { useCreateController } from "../../../store/CreateComponentStore";
import { usePageController } from "../../../store/PageController";
import Loading from "../../UI/Loading/Loading";
import ComplilationModal from "../ComplilationModal";
import { useNavigate } from "react-router-dom";

const programming_languages = ["Go", "Python", "Web Server"];

const Create = () => {
  const [loading, setLoading] = useState(false);
  const [funcNameError, setFuncNameError] = useState("");
  const [hostnameError, setHostnameError] = useState("");
  const [uriError, setUriError] = useState("");
  const [fileName, setFileName] = useState("");
  const [compError, setCompError] = useState(false);
  const [compMessage, setCompMessage] = useState("");
  const navigate = useNavigate();

  const smallerThan900 = useMediaQuery("(min-width:900px");

  const data = useCreateController((state) => state.data);
  const setCurrentComponent = usePageController(
    (state) => state.setCurrentComponent
  );

  const setData = useCreateController((state) => state.setData);
  const clearData = useCreateController((state) => state.clearData);

  useEffect(() => {
    setData("code", defaultCodes(data.currentLanguage));
  }, []);

  const validateHostname = (value) => {
    let pattern = /^(\*\.)?([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/;

    if (data.protocol === "https") {
      if (pattern.test(value)) {
        return true;
      } else {
        return false;
      }
    } else {
      if (data.protocol === "http") {
        if (value.length > 0) {
          if (pattern.test(value)) {
            return true;
          } else {
            return false;
          }
        }

        return true;
      }
    }
  };

  const validateUri = (value) => {
    let pattern = /^(\/[a-z-*]+)+$|(^\/$)/;
    if (pattern.test(value) && value.includes("/")) {
      return true;
    }
    return false;
  };

  const closeLoadingHandler = () => {
    setLoading(false);
  };

  const validateFuncName = (value) => {
    if (value.trim().length < 1) {
      return false;
    }
    if (value.length >= 50) {
      return false;
    }

    return true;
  };

  const clearErrors = () => {
    setUriError("");
    setHostnameError("");
    setFuncNameError("");
  };

  const languageChangeHandler = (e) => {
    clearErrors();
    if (e.target.value === "Web Server") {
      setData("currentLanguage", e.target.value);
      setData("code", null);

      setData("hostname", "");

      return;
    }

    const sample_code = defaultCodes(e.target.value);

    setData("currentLanguage", e.target.value);
    setData("code", sample_code);
    setData("funcName", "");
    setData("hostname", "");
  };

  const codeChangeHandler = (text) => {
    clearErrors();
    setData("code", text);
  };

  const handleFileChange = (event) => {
    clearErrors();
    const selectedFile = event.target.files[0];

    const reader = new FileReader();
    reader.onload = function (e) {
      setData("file", e.target.result);
    };
    reader.readAsDataURL(selectedFile);

    if (selectedFile) {
      setFileName(selectedFile.name);
      setData("funcName", selectedFile.name.toString());
    } else {
      console.error("No File");
    }
  };

  const closeCompHandler = () => {
    setCompError(false);
    setLoading(false);
  };

  const deployHandler = async (e) => {
    e.preventDefault();
    setLoading(true);

    const isUriValid = validateUri(data.uri);
    const isHostnameValid = validateHostname(data.hostname);
    const isFuncNameValid = validateFuncName(data.funcName);

    setUriError(!isUriValid ? "Invalid URI" : "");
    setHostnameError(!isHostnameValid ? "Invalid Hostname" : "");
    setFuncNameError(!isFuncNameValid ? "Invalid Function Name" : "");

    if (data.currentLanguage === "Web Server") {
      if (!isUriValid) {
        setLoading(false);
        return;
      }
    } else {
      if (!isUriValid || !isHostnameValid || !isFuncNameValid) {
        setLoading(false);
        return;
      }
    }

    let url = "https://rawdat.ai/login/portal/deploy-function";
    const formData = JSON.stringify(data);

    const response = await fetch(url, {
      method: "POST",
      body: formData,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (response.status === 401) {
      setLoading(false);
      navigate("/login");
      return;
    }

    if (!response?.ok || response.status === 500) {
      setLoading(false);
      const response_json = await response.text();

      setCompMessage(response_json);
      setCompError(true);
      return;
    }

    if (response.ok) {
      setLoading(false);
      setCurrentComponent("deployments");
    }
  };

  return (
    <>
      {loading && <Loading open={loading} closeLoading={closeLoadingHandler} />}
      {compError && (
        <ComplilationModal
          open={compError}
          onClose={closeCompHandler}
          message={compMessage}
        />
      )}
      <Box
        width={smallerThan900 ? "calc(1064px - 17px)" : "90vw"}
        display={"flex"}
        flexDirection={"column"}
        gap={"1.5rem"}
        marginBottom={"300px"}
      >
        {/* Select programming language */}
        <Typography variant="title"> Create </Typography>
        <MUICard>
          <Typography>Select a programming language</Typography>

          <Select
            placeholder="Select"
            value={data.currentLanguage}
            sx={{
              width: "250px",
              backgroundColor: "#ffffff",
              "&:hover": { backgroundColor: "#ffffff" },
            }}
            onChange={languageChangeHandler}
            required
          >
            {programming_languages.map((lang) => (
              <MenuItem key={lang} value={lang}>
                {lang}
              </MenuItem>
            ))}
          </Select>
        </MUICard>

        {/* Function name */}
        {data.currentLanguage !== "Web Server" && (
          <MUICard>
            <Typography>Function name</Typography>
            <TextField
              className="smaller"
              value={data.funcName}
              sx={{ width: "465px", backgroundColor: "#ffffff" }}
              placeholder="example"
              onChange={(e) => {
                setFuncNameError("");
                setData("funcName", e.target.value);
              }}
              required
              error={!!funcNameError}
              helperText={funcNameError}
            />
          </MUICard>
        )}

        {/* Code */}
        {data.currentLanguage !== "Web Server" && (
          <MUICard>
            <Typography>Code</Typography>
            <Editor
              height="40vh"
              defaultLanguage={data.currentLanguage.toLowerCase()}
              value={data.code}
              theme="vs-dark"
              onChange={codeChangeHandler}
            />
          </MUICard>
        )}

        {/* URI */}
        <MUICard>
          <Typography>{`Uniform Resource Identifier (URI)`}</Typography>
          <TextField
            value={data.uri}
            sx={{
              width: "465px",
              backgroundColor: "#ffffff",
            }}
            className="smaller"
            placeholder="example"
            onChange={(e) => {
              setUriError("");
              setData("uri", e.target.value);
            }}
            error={!!uriError}
            helperText={uriError}
            required
          />
        </MUICard>

        {data.currentLanguage !== "Web Server" && (
          <Box display={"flex"} gap={"1.5rem"}>
            {/* PROTOCOL */}

            <MUICard>
              <Typography>Protocol</Typography>
              <RadioGroup
                aria-labelledby="protocol"
                defaultValue={data.protocol}
                value={data.protocol}
                name="protocol"
                onChange={(e) => {
                  setHostnameError("");
                  setData("protocol", e.target.value);
                }}
              >
                <FormControlLabel
                  value="http"
                  control={<Radio />}
                  label="HTTP"
                />
                <FormControlLabel
                  value="https"
                  control={<Radio />}
                  label="HTTPS"
                />
              </RadioGroup>
            </MUICard>

            {/* HOSTNAME */}
            <MUICard>
              <Typography>
                Hostname{" "}
                {data.protocol === "http" ? (
                  <span className="disabled-text">(optional)</span>
                ) : (
                  ""
                )}
              </Typography>
              <TextField
                value={data.hostname}
                sx={{ width: "465px", backgroundColor: "#ffffff" }}
                className="smaller"
                placeholder="example"
                onChange={(e) => {
                  setHostnameError("");
                  setData("hostname", e.target.value);
                }}
                required={data.protocol === "https" ? true : false}
                error={!!hostnameError}
                helperText={hostnameError}
              />
            </MUICard>
          </Box>
        )}

        {/* Upload File */}

        {data.currentLanguage === "Web Server" && (
          <MUICard>
            <Typography>Upload File</Typography>

            <Box display={"flex"} gap={"1rem"}>
              <Box>
                <input
                  type="file"
                  style={{ display: "none" }}
                  id="file-input"
                  onChange={handleFileChange}
                />
                <label htmlFor="file-input">
                  <Chip
                    label={"Choose file"}
                    component="span"
                    clickable
                    color="primary"
                    variant="outlined"
                  />
                </label>
              </Box>

              {!!fileName && <Typography>{fileName}</Typography>}
            </Box>
          </MUICard>
        )}

        {/* Buttons */}
        <Box display={"flex"} gap={"1rem"}>
          {data.currentLanguage !== "Web Server" && (
            <Chip
              label="Clear"
              variant="outlined"
              color="primary"
              sx={{
                width: "100px",
                cursor: "pointer",
                "& .MuiChip-label": {
                  padding: 0,
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: "20px",
                },
              }}
              onClick={() => {
                clearErrors();
                clearData();
              }}
            />
          )}
          <Chip
            label="Deploy"
            variant="filled"
            color="primary"
            sx={{ width: "100px", cursor: "pointer" }}
            onClick={deployHandler}
          />
        </Box>
      </Box>
    </>
  );
};

export default Create;
